import {
  lazy,
  useCallback,
  ReactNode,
  Suspense,
  useState,
  useRef,
  useEffect,
  MouseEvent,
} from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import { CellNoteConfig, getCellKey } from '@common/interfaces/notes'
import cn from 'classnames'
import queryString from 'query-string'
import { currentWorkFlow } from '../../../../helpers/helpers'
import Icon from '../../Icon'
import Menu from '../../Menu'
import CellAttributionMarker from '../CellAttributionMarker'
import CellContextPanel from '../CellContextPanel'
import CellNoteIndicator from '../CellNoteIndicator/CellNoteIndicator'
import { useCellNotesContext } from '../CellNotesProvider'
import styles from './CellWithNotes.module.scss'
import { PropsFromRedux } from './index'

// Every table cell renders this; the note editor (draft-js) loads when a note is first started.
const InlineNoteEditor = lazy(() => import('../InlineNoteEditor/InlineNoteEditor'))

const CONTEXT_MENU_LIST_PROPS = { className: styles.contextMenuList }

export interface IProps extends PropsFromRedux {
  cellNoteConfig: CellNoteConfig
  children: ReactNode
  className?: string
}

const CellWithNotes = ({ cellNoteConfig, children, className, toggleNotesOn }: IProps) => {
  const { getCellNoteCount, cellHasNotes, getCellAttribution, config, createNote } =
    useCellNotesContext()
  const [isContextPanelOpen, setIsContextPanelOpen] = useState(false)
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
  } | null>(null)
  const [noteEditorPosition, setNoteEditorPosition] = useState<{
    mouseX: number
    mouseY: number
  } | null>(null)

  const cellRef = useRef<HTMLDivElement>(null)
  const { pathname, search, hash } = useLocation()
  const { id, clientId: overrideIdParam } = useParams<{
    id: string
    clientId: string
  }>()
  const navigate = useNavigate()
  const {
    rowId,
    columnId,
    value,
    recordDate: cellRecordDate,
    align = 'right',
    isTableFilterRowCell,
    canExpand = false,
    isExpanded = false,
    onExpandFields,
    onCollapseFields,
    entityId,
    clientId,
    userId,
    prospectEntityId,
  } = cellNoteConfig

  // Use table recordDate when cell doesn't provide one so create and lookup use the same key
  const effectiveRecordDate =
    cellRecordDate ?? (Array.isArray(config.recordDate) ? undefined : config.recordDate)

  const cellKey = getCellKey(rowId, columnId, effectiveRecordDate)
  const hasNotes = cellHasNotes(rowId, columnId, effectiveRecordDate)
  const noteCount = getCellNoteCount(rowId, columnId, effectiveRecordDate)
  const { suggestedCount, establishedCount, establishedNoteCount, confirmedLinkCount } =
    getCellAttribution(rowId, columnId, effectiveRecordDate)
  // One rule governs both markers: the solid one counts what the drawer shows, the dashed one
  // counts what is waiting on a decision. An approved suggestion and an established fact are
  // both simply "on this cell" to a reader, and both are listed in the drawer, so both belong
  // to the solid count — which is what keeps the number and the list from ever disagreeing.
  const solidMarkerCount = noteCount + confirmedLinkCount + establishedCount
  const hasSolidMarker = hasNotes || confirmedLinkCount > 0 || establishedCount > 0
  const hasPendingReview = suggestedCount > 0

  useEffect(() => {
    const handleCloseAllEditors = (event: CustomEvent) => {
      const { exceptCellKey } = event.detail as { exceptCellKey?: string }
      if (exceptCellKey !== cellKey) {
        setIsAddingNote(false)
      }
    }

    window.addEventListener('closeAllNoteEditors', handleCloseAllEditors as EventListener)

    return () => {
      window.removeEventListener('closeAllNoteEditors', handleCloseAllEditors as EventListener)
    }
  }, [cellKey])

  // Handle cell highlighting when notes are clicked
  useEffect(() => {
    const handleHighlightCell = (event: Event) => {
      const customEvent = event as CustomEvent<{
        rowId: string
        columnId: string
        recordDate?: string | null
        tableType: string
      }>
      const {
        rowId: targetRowId,
        columnId: targetColumnId,
        recordDate: targetRecordDate,
        tableType: targetTableType,
      } = customEvent.detail as {
        rowId?: string
        columnId?: string
        recordDate?: string
        tableType?: string
      }
      const targetCellKey = getCellKey(targetRowId, targetColumnId, targetRecordDate)

      // Only highlight if this is the target cell and table types match
      if (cellKey === targetCellKey && config.tableType === targetTableType) {
        // Apply highlighting to the parent TD or TH element
        const cellElement = cellRef.current?.closest('td') || cellRef.current?.closest('th')
        if (cellElement) {
          cellElement.classList.add('highlighted-cell')

          // Remove highlight after 3 seconds
          setTimeout(() => {
            cellElement.classList.remove('highlighted-cell')
          }, 3000)
        }
      }
    }

    window.addEventListener('highlightCell', handleHighlightCell)

    return () => {
      window.removeEventListener('highlightCell', handleHighlightCell)
    }
  }, [cellKey, config.tableType])

  const handleNoteClick = useCallback(
    (forceShowNotes = false) => {
      const isColumnOrRowHeader = !rowId || !columnId
      if (hasNotes || forceShowNotes || isColumnOrRowHeader) {
        const currentQuery = queryString.parse(search)
        const newQuery = {
          ...currentQuery,
          cellNoteContext: JSON.stringify({
            tableType: config.tableType,
            value,
            rowId,
            columnId,
            periodView: config.periodView,
            ...(effectiveRecordDate != null && { recordDate: effectiveRecordDate }),
          }),
        }

        toggleNotesOn()

        void navigate({
          pathname,
          search: queryString.stringify(newQuery),
          hash,
        })
      } else {
        const currentQuery = queryString.parse(search)
        delete currentQuery.cellNoteContext
        void navigate({
          pathname,
          search: queryString.stringify(currentQuery),
          hash,
        })
      }
    },
    [
      rowId,
      columnId,
      value,
      effectiveRecordDate,
      toggleNotesOn,
      search,
      hash,
      config.tableType,
      config.periodView,
      navigate,
      pathname,
      hasNotes,
    ],
  )

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()

      const target = event.target as HTMLElement
      const cellElement = target.closest('[data-cell-key]')
      const clickedCellKey = cellElement?.getAttribute('data-cell-key')

      if (clickedCellKey !== cellKey) {
        return
      }

      setIsAddingNote(false)

      window.dispatchEvent(
        new CustomEvent('closeAllNoteEditors', {
          detail: { exceptCellKey: cellKey },
        }),
      )

      const newContextMenu = {
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      }

      setContextMenu(newContextMenu)
    },
    [cellKey],
  )

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  const handleCopyCell = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (value !== undefined && value !== null) {
        void navigator.clipboard.writeText(String(value))
      }
      handleCloseContextMenu()
    },
    [value, handleCloseContextMenu],
  )

  const handleAddCellNote = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      if (contextMenu) {
        setNoteEditorPosition({
          mouseX: contextMenu.mouseX,
          mouseY: contextMenu.mouseY + 10,
        })
      }

      handleCloseContextMenu()

      window.dispatchEvent(
        new CustomEvent('closeAllNoteEditors', {
          detail: { exceptCellKey: cellKey },
        }),
      )

      setIsAddingNote(true)
    },
    [handleCloseContextMenu, cellKey, contextMenu],
  )

  const handleExpandFields = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onExpandFields?.()
      handleCloseContextMenu()
    },
    [onExpandFields, handleCloseContextMenu],
  )

  const handleCollapseFields = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onCollapseFields?.()
      handleCloseContextMenu()
    },
    [onCollapseFields, handleCloseContextMenu],
  )

  // Handle note creation
  const handleCreateNote = useCallback(
    async (noteData: Record<string, unknown>) => {
      const workflow = currentWorkFlow(pathname)

      await createNote(
        {
          rowId,
          columnId,
          value,
          recordDate: effectiveRecordDate,
        },
        {
          ...noteData,
          workflow,
          id: overrideIdParam || id,
          cellNoteEntityIds: {
            ...(entityId && { entityId }),
            ...(clientId && { clientId }),
            ...(userId && { userId }),
            ...(prospectEntityId && { prospectEntityId }),
          },
        },
      )
      setIsAddingNote(false)
    },
    [
      createNote,
      rowId,
      columnId,
      effectiveRecordDate,
      id,
      pathname,
      value,
      overrideIdParam,
      entityId,
      clientId,
      userId,
      prospectEntityId,
    ],
  )

  // Handle cancel note creation
  const handleCancelNote = useCallback(() => {
    setIsAddingNote(false)
    setNoteEditorPosition(null)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (contextMenu !== null) {
        const target = event.target as HTMLElement
        if (!target.closest('[data-context-menu]')) {
          setContextMenu(null)
        }
      }
    }

    const handleContextMenuOutside = () => {
      if (contextMenu !== null) {
        setContextMenu(null)
      }
    }

    if (contextMenu !== null) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
        document.addEventListener('contextmenu', handleContextMenuOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('click', handleClickOutside)
        document.removeEventListener('contextmenu', handleContextMenuOutside)
      }
    }
  }, [contextMenu])

  const handleOpenContextPanel = useCallback(() => setIsContextPanelOpen(true), [])

  // The solid marker means two different things now. With a real note it opens the notes
  // drawer, as it always has. When it is standing in for confirmed links alone, that drawer
  // has nothing to show — handleNoteClick bails out unless the cell actually has notes — so
  // it opens the context panel, which is the only place confirmed links are rendered.
  // Without this the marker would be inert and the confirmed link unreachable, since the
  // suggestion ring disappears the moment it is approved.
  // Always the drawer, forced: the marker can stand for a cell whose only content is an
  // established fact or an approved suggestion, and handleNoteClick's own guard turns on
  // authored notes, which such a cell has none of.
  const handleSolidMarkerClick = useCallback(() => handleNoteClick(true), [handleNoteClick])
  const handleCloseContextPanel = useCallback(() => setIsContextPanelOpen(false), [])

  const handleLoadNotesAfterCreate = useCallback(() => {
    handleNoteClick(true)
  }, [handleNoteClick])

  // Handle click on the cell (not the note indicator)
  const handleCellClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement
      // Also treat in-cell editable fields as interactive. A SelectField/date
      // picker in a table isn't a native control — when resting it's a
      // `.syntheticSelectField`/`.syntheticInputField` div and when active it's
      // MUI's `.MuiSelect-select[role="combobox"]` — so without these the click
      // is swallowed here as a note-filter action and the editor never opens.
      const isInteractiveElement = target.closest(
        'a, button, input, select, textarea, [role="button"], [role="combobox"], ' +
          '.MuiSelect-select, .MuiInputBase-root, .syntheticSelectField, .syntheticInputField, ' +
          '[data-input-zone]',
      )

      if (isInteractiveElement) {
        return
      }

      // Determine if this is a column header (not row header)
      const isColumnHeader = isTableFilterRowCell || (!rowId && columnId)

      // For regular cells and row headers, clicking applies note filtering/unfiltering
      // Row headers should filter for all notes in that row
      // For column headers, clicking should not apply note filtering (only sorting, which is handled by the parent)
      if (!isColumnHeader) {
        handleNoteClick()
      }
    },
    [isTableFilterRowCell, rowId, columnId, handleNoteClick],
  )

  if (!config.enabled) {
    return children
  }

  return (
    <>
      <div
        ref={cellRef}
        data-cell-key={cellKey}
        className={cn(styles.root, className, {
          [styles.isAddingNote]: isAddingNote,
          [styles.headerCell]: isTableFilterRowCell,
        })}
        onContextMenu={handleContextMenu}
        onClick={handleCellClick}
      >
        <div
          className={cn(styles.cellContent, {
            [styles.alignLeft]: align === 'left',
            [styles.headerCellContent]: isTableFilterRowCell,
          })}
        >
          {children}
        </div>

        {isAddingNote && (
          <Suspense fallback={null}>
            <InlineNoteEditor
              key={cellKey}
              handleLoadNotesAfterCreate={handleLoadNotesAfterCreate}
              onSubmit={handleCreateNote}
              onCancel={handleCancelNote}
              anchorEl={isTableFilterRowCell ? cellRef.current?.parentElement : cellRef.current}
              anchorPosition={
                noteEditorPosition
                  ? {
                      top: noteEditorPosition.mouseY,
                      left: noteEditorPosition.mouseX,
                    }
                  : undefined
              }
              className={cn({
                [styles.headerCellPopover]: isTableFilterRowCell,
              })}
            />
          </Suspense>
        )}
      </div>

      {/* Leaves focus in the cell; the outside-click listener above does the dismissing. */}
      <Menu
        anchorPosition={contextMenu && { top: contextMenu.mouseY, left: contextMenu.mouseX }}
        onClose={handleCloseContextMenu}
        isNonBlocking
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        paperClassName={styles.contextMenuPaper}
        menuListProps={CONTEXT_MENU_LIST_PROPS}
      >
        <MenuItem onClick={handleAddCellNote}>
          <ListItemIcon className={styles.menuIcon}>
            <Icon name="note-add" size="sm" />
          </ListItemIcon>
          <ListItemText>Add Note</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyCell}>
          <ListItemIcon className={styles.menuIcon}>
            <Icon name="copy" size="sm" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        {canExpand && !isExpanded && (
          <MenuItem onClick={handleExpandFields}>
            <ListItemIcon>
              <Icon name="chevron-down" size="sm" />
            </ListItemIcon>
            <ListItemText>Expand Fields</ListItemText>
          </MenuItem>
        )}
        {canExpand && isExpanded && (
          <MenuItem onClick={handleCollapseFields}>
            <ListItemIcon>
              <Icon name="chevron-up" size="sm" />
            </ListItemIcon>
            <ListItemText>Collapse Fields</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {hasSolidMarker && (
        <CellNoteIndicator
          noteCount={solidMarkerCount}
          hasPendingReview={hasPendingReview}
          isEstablished={establishedNoteCount > 0}
          onClick={handleSolidMarkerClick}
        />
      )}

      {/*
        Always rendered when the cell carries attribution, because it is the only way into the
        context panel — the solid marker opens the notes drawer when a note exists. Beside an
        existing marker it becomes the accent, positioned clear of the count bubble that
        replaces the dog-ear on hover. It owns its own click, so the cell keeps left-click
        select and right-click copy / add-note.
      */}
      {hasPendingReview && (
        <CellAttributionMarker
          suggestedCount={suggestedCount}
          variant={hasSolidMarker ? 'accent' : 'ring'}
          onClick={handleOpenContextPanel}
        />
      )}

      {isContextPanelOpen && (
        <CellContextPanel
          tableType={String(config.tableType)}
          clientId={config.clientId}
          workflow={config.workflow}
          id={config.id}
          rowId={rowId}
          columnId={columnId}
          periodView={config.periodView}
          recordDate={effectiveRecordDate ?? undefined}
          anchorEl={cellRef.current}
          open={isContextPanelOpen}
          onClose={handleCloseContextPanel}
        />
      )}
    </>
  )
}

export default CellWithNotes
